import { useCallback } from "react";
import type { ReactNode, RefObject } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import type { EditableDataGridHandle } from "@/components/editable-data-grid";
import { CSICodePicker } from "@/components/csi-code-picker";
import {
  ContractorDropdown,
  DateDropdown,
  DurationDropdown,
  LocationDropdown,
  ProjectItemDropdown,
  TeamMemberDropdown,
} from "@/components/submittal/column-dropdowns";
import type { OrgMemberOption } from "./submittal-config-controls";
import type { SubmittalColumnMap } from "./submittal-columns";

interface NamedCustomItem {
  _id: string;
  name: string;
  type?: string;
}

export type TemplateConfigColumn = {
  key: string;
  header: string;
  type: "team_member" | "project_item" | "duration" | "date";
  columnIndex: number;
  defaultValue?: string;
};

function projectItemTypeForColumn(columnKey: string) {
  if (columnKey === "location") return "location";
  if (columnKey.includes("contractor")) return "contractor";
  return `project_item:${columnKey}`;
}

interface UseSubmittalCellRendererArgs {
  columns: SubmittalColumnMap;
  gridRef: RefObject<EditableDataGridHandle | null>;
  displayRows?: string[][];
  projectId?: Id<"projects">;
  locations: NamedCustomItem[];
  contractors: NamedCustomItem[];
  orgMembers: OrgMemberOption[];
  projectCustomItems: NamedCustomItem[];
  templateConfigColumns: TemplateConfigColumn[];
  createCustomItem: (args: {
    projectId: Id<"projects">;
    type: string;
    name: string;
  }) => Promise<unknown>;
}

export function useSubmittalCellRenderer({
  columns,
  gridRef,
  displayRows,
  projectId,
  locations,
  contractors,
  orgMembers,
  projectCustomItems,
  templateConfigColumns,
  createCustomItem,
}: UseSubmittalCellRendererArgs) {
  return useCallback(
    (
      rowIndex: number,
      colIndex: number,
      value: string,
      onChange: (value: string) => void
    ): ReactNode | null => {
      if (colIndex === columns.csiCode && columns.csiCode >= 0) {
        return (
          <CSICodePicker
            value={value}
            onSelect={(code, title, divisionCode, divisionTitle) => {
              const oldCode = value.trim();
              const newCode = code.trim();

              onChange(code);
              
              const updates: Array<{ row: number; col: number; value: string }> = [];
              
              if (columns.csiTitle >= 0) {
                updates.push({ row: rowIndex, col: columns.csiTitle, value: title });
              }
              
              // Auto-populate Package Spec Section columns with division info
              if (divisionCode && columns.packageSpecSectionNumber >= 0) {
                updates.push({ row: rowIndex, col: columns.packageSpecSectionNumber, value: divisionCode });
              }
              if (divisionTitle && columns.packageSpecSectionDescription >= 0) {
                updates.push({ row: rowIndex, col: columns.packageSpecSectionDescription, value: divisionTitle });
              }
              
              if (updates.length > 0) {
                gridRef.current?.updateCells(updates);
              }

              if (columns.submittalNumber >= 0 && oldCode !== newCode) {
                const allRows = gridRef.current?.getRows() ?? displayRows;
                if (!allRows) return;

                const affectedCodes = new Set([oldCode, newCode].filter(Boolean));
                if (affectedCodes.size === 0) return;

                const submittalUpdates: Array<{ row: number; col: number; value: string }> = [];
                const sequences: Record<string, number> = {};

                for (let i = 0; i < allRows.length; i++) {
                  const rowCsi = (i === rowIndex ? newCode : allRows[i]?.[columns.csiCode] ?? "").trim();
                  if (!rowCsi || !affectedCodes.has(rowCsi)) continue;
                  sequences[rowCsi] = (sequences[rowCsi] ?? 0) + 1;
                  submittalUpdates.push({
                    row: i,
                    col: columns.submittalNumber,
                    value: `${rowCsi}-${sequences[rowCsi]}`,
                  });
                }

                if (submittalUpdates.length > 0) {
                  gridRef.current?.updateCells(submittalUpdates);
                }
              }
            }}
          />
        );
      }

      if (
        colIndex === columns.csiTitle &&
        columns.csiTitle >= 0 &&
        columns.csiCode >= 0
      ) {
        return (
          <div
            className="min-w-[40px] whitespace-normal break-words rounded-sm px-1 py-0.5 text-[10px]"
            title={value}
          >
            {value || <span className="text-muted-foreground">-</span>}
          </div>
        );
      }

      const templateColumn = templateConfigColumns.find((column) => column.columnIndex === colIndex);
      if (templateColumn?.type === "team_member") {
        return (
          <TeamMemberDropdown
            members={orgMembers}
            value={value}
            onChange={onChange}
            placeholder={`Select ${templateColumn.header.toLowerCase()}`}
          />
        );
      }

      if (templateColumn?.type === "duration" && projectId) {
        return (
          <DurationDropdown
            value={value}
            onChange={onChange}
            items={projectCustomItems.filter((item) => item.type === "duration")}
            projectId={projectId}
            onAddNew={async (name) => {
              await createCustomItem({ projectId, type: "duration", name });
            }}
          />
        );
      }

      if (templateColumn?.type === "duration") {
        return <DurationDropdown value={value} onChange={onChange} />;
      }

      if (templateColumn?.type === "date") {
        return <DateDropdown value={value} onChange={onChange} />;
      }

      if (templateColumn?.type === "project_item" && projectId) {
        const itemType = projectItemTypeForColumn(templateColumn.key);
        return (
          <ProjectItemDropdown
            items={projectCustomItems.filter((item) => item.type === itemType)}
            itemLabel={templateColumn.header}
            value={value}
            onChange={onChange}
            onAddNew={async (name) => {
              await createCustomItem({
                projectId,
                type: itemType,
                name,
              });
            }}
          />
        );
      }

      if (colIndex === columns.location && columns.location >= 0 && projectId) {
        return (
          <LocationDropdown
            locations={locations}
            value={value}
            onChange={onChange}
            onAddNew={async (name) => {
              await createCustomItem({
                projectId,
                type: "location",
                name,
              });
            }}
            placeholder="Select location"
          />
        );
      }

      if (colIndex === columns.contractor && columns.contractor >= 0 && projectId) {
        return (
          <ContractorDropdown
            contractors={contractors}
            value={value}
            onChange={onChange}
            onAddNew={async (name) => {
              await createCustomItem({
                projectId,
                type: "contractor",
                name,
              });
            }}
            placeholder="Select contractor"
          />
        );
      }

      return null;
    },
    [columns, gridRef, displayRows, projectId, locations, contractors, orgMembers, projectCustomItems, templateConfigColumns, createCustomItem]
  );
}

export { projectItemTypeForColumn };
